import { Form, Link, redirect } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, buttonVariants } from '~/common/components/ui/button';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '~/common/components/ui/card';
import { optionalProtectedLoader } from '~/shell/utils/authUtil.server';
import type { Route } from './+types/AreaSelectionRoute';
import { getRootRoute } from '~/shell/utils/routeUtil.server';
import { LucideHandshake, LucideShoppingCart, LucideTruck, LucideUser } from 'lucide-react';
import { getLanguageValue, webshopConfig } from '~/config/webshopConfig';
import { getLinkRel } from '~/common/utils/urlUtil';
import { LanguageSwitcher } from '~/shell/components/LanguageSwitcher';
import { cn } from '~/common/utils/styleUtil';
import { LoginButton } from '~/shell/components/LoginButton';
import { PgArea, TgArea } from '~/common/types/area';
import { CUSTOMER_TYPE_AGH, CUSTOMER_TYPE_BGH, CUSTOMER_TYPE_MULTICHANNEL } from '~/shell/types/sessionCustomer';
import TransgourmetProdegaLogo from '~/assets/logos/transgourmet-prodega-logo';
import { authLogout } from '~/shell/oauth.server.ts';
import { sanitizeRedirectUrl } from '~/common/utils/redirectUtil.ts';
import { createRequestApi } from '~/common/utils/requestApi.server.ts';
import type { ReactNode } from 'react';

enum AreaSelectionCase {
  ANONYMOUS,
  AGH,
  BGH
}

const ACTION_INTENT = {
  logoutAndRedirect: 'logoutAndRedirect',
  redirectUrl: 'redirectUrl'
};

export const loader = optionalProtectedLoader(
  async ({ loginInfo, lang }) => {
    // Redirect to the respective area if the user is already logged in (Multichannel is handled as BGH)
    if (loginInfo?.customerType === CUSTOMER_TYPE_AGH) {
      throw redirect(`/${lang}/${PgArea}`);
    } else if (
      loginInfo?.customerType === CUSTOMER_TYPE_BGH ||
      loginInfo?.customerType === CUSTOMER_TYPE_MULTICHANNEL
    ) {
      throw redirect(`/${lang}/${TgArea}`);
    }

    return { loginInfo, lang };
  },
  { skipAreaCheck: true, skipLangCheck: false }
);

export const action = async ({ request, context }) => {
  const api = createRequestApi();
  const formData = await request.formData();
  const intent = formData.get(FORM_FIELDS.Intent);
  const fallback = getRootRoute(request, context);
  const redirectUrl = sanitizeRedirectUrl(formData.get(ACTION_INTENT.redirectUrl) as string | null, fallback);

  if (intent === ACTION_INTENT.logoutAndRedirect) {
    await authLogout(api, redirectUrl);
  }

  return null;
};

interface AreaSelectorItemProps {
  title: string;
  description?: string;
  forceLogout?: boolean;
  link: string;
  actionText: string;
  area: 'webshop' | 'prodega-easy';
  variant?: 'default' | 'secondary' | 'primaryOutline';
}

export function AreaSelectorItem({
  title,
  description,
  forceLogout = false,
  link,
  actionText,
  area,
  variant = 'primaryOutline'
}: Readonly<AreaSelectorItemProps>) {
  const { t } = useTranslation();

  return (
    <Card className={'flex flex-col justify-between w-full desktop:w-sm'}>
      <CardHeader className={'flex flex-row desktop:flex-col gap-4 desktop:gap-0 pb-3 desktop:pb-4'}>
        {area === PgArea ? (
          <LucideShoppingCart className={'w-10 h-10 desktop:w-12 desktop:h-12'} />
        ) : (
          <LucideTruck className={'w-12 h-12 desktop:w-12 desktop:h-12'} />
        )}
        <CardTitle className={'desktop:pt-4 desktop:min-h-16 leading-snug text-lg desktop:text-2xl'}>{title}</CardTitle>
      </CardHeader>
      {(description || forceLogout) && (
        <CardContent>
          {description && <p>{description}</p>}
          {forceLogout && <strong>{t('Sie werden automatisch abgemeldet.')}</strong>}
        </CardContent>
      )}
      <CardFooter>
        {forceLogout ? (
          <Form method={'POST'}>
            <input type="hidden" name={ACTION_INTENT.redirectUrl} value={link} />
            <Button
              name={FORM_FIELDS.Intent}
              value={ACTION_INTENT.logoutAndRedirect}
              type="submit"
              variant={'secondary'}
              data-testid={`area-selector-item-${link}`}
            >
              {actionText}
            </Button>
          </Form>
        ) : (
          <Link to={link} className={buttonVariants({ variant: variant })} data-testid={`area-selector-item-${link}`}>
            {actionText}
          </Link>
        )}
      </CardFooter>
    </Card>
  );
}

interface SelectorItemProps {
  icon: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
}

function SelectorItem({ title, icon, description, children }: Readonly<SelectorItemProps>) {
  return (
    <Card className={'flex flex-col justify-between w-full desktop:w-sm'}>
      <CardHeader className={'flex flex-row gap-4 pb-3 desktop:pb-4'}>
        {icon}
        <CardTitle className={'desktop:pt-4 desktop:min-h-16 leading-snug text-lg desktop:text-2xl'}>{title}</CardTitle>
      </CardHeader>
      {description && (
        <CardContent>
          <p>{description}</p>
        </CardContent>
      )}
      <CardFooter>{children}</CardFooter>
    </Card>
  );
}

export default function AreaSelectionRoute({ loaderData }: Route.ComponentProps) {
  const { loginInfo, lang } = loaderData;
  const { t, i18n } = useTranslation();

  let customerCase = AreaSelectionCase.ANONYMOUS;
  if (loginInfo) {
    customerCase = loginInfo.customerType === 'AGH' ? AreaSelectionCase.AGH : AreaSelectionCase.BGH;
  }

  return (
    <div className={'flex flex-col min-h-screen w-full'}>
      <div className={''}>
        <div className={'flex justify-end py-2 px-4'}>
          <LanguageSwitcher />
        </div>
      </div>
      <div className="flex-1 w-full flex flex-col items-center justify-center p-4">
        <div className="flex justify-center pb-8">
          <div className="w-[260px] desktop:w-[360px] flex flex-col justify-center items-center">
            <TransgourmetProdegaLogo />
          </div>
        </div>

        <div className={'grid grid-cols-1 md:grid-cols-2 gap-4 '}>
          {customerCase === AreaSelectionCase.ANONYMOUS && (
            <>
              <AreaSelectorItem
                area={PgArea}
                title={t('Ich möchte zu Prodega Easy (Abholung).')}
                link={`/${lang}/${PgArea}`}
                actionText={t('Zu Prodega-Easy')}
              />
              <AreaSelectorItem
                area={TgArea}
                title={t('Ich möchte zum Transgourmet-Webshop (Belieferung).')}
                link={`/${lang}/${TgArea}`}
                actionText={t('Zum Transgourmet-Webshop')}
              />
            </>
          )}

          <SelectorItem
            icon={<LucideUser className={'w-8 h-8 desktop:w-12 desktop:h-12'} />}
            title={t('Ich möchte mich anmelden.')}
          >
            <LoginButton hideIcon={true} redirectPath={`/${lang}`} />
          </SelectorItem>

          <SelectorItem
            icon={<LucideHandshake className={'w-8 h-8 desktop:w-12 desktop:h-12'} />}
            title={t('Ich möchte Kunde werden.')}
          >
            <a
              className={cn(buttonVariants({ variant: 'primaryOutline' }))}
              href={getLanguageValue(lang ?? i18n.language, webshopConfig.registrationLink)}
              target={'_blank'}
              rel={getLinkRel(getLanguageValue(lang ?? i18n.language, webshopConfig.registrationLink))}
            >
              {t('Kunde werden')}
            </a>
          </SelectorItem>
        </div>
      </div>
    </div>
  );
}
